import { IonItem, IonLabel, IonList } from '@ionic/react';
import * as React from 'react';

import { IntegrationSystem } from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import { str } from '~/app/js/localization/localization';
import { GcnCustomerAccountHelper } from '~/helpers';
import StoredValueHelper from '~/helpers/stored_value_helper';
import type { CheckoutState } from '~/stores/checkout.store';

import type { Store } from '../stores';
import { useLocation, useSettings } from '../stores';
import type { EcommI9nConfig, KioskMenuSettings } from '../types';
import { Coupon } from './coupon';
import { FreedomPayEcommForm } from './ecomm-payment/freedom-pay/freedom-pay-ecomm-form';
import type { FreedomPayEcommUtils } from './ecomm-payment/freedom-pay/freedom-pay-ecomm-form.utils';
import { StripeEcommForm } from './ecomm-payment/stripe/stripe-ecomm-form';
import SubmitOrderButton from './ecomm-payment/submit-order-button';
import { Loyalty } from './loyalty';
import { OrderTotals } from './order-totals';
import StoredValuePanel from './stored-value-panel';
import { TipSelector } from './tip-selector';

type PaymentDetailsProps = {
  ecommI9nConfig: EcommI9nConfig;
  onSubmit(queuePath?: string): void;
  checkoutState: Store['checkout'];
  loyaltyState: Store['loyalty'];
};

export function PaymentDetails(props: PaymentDetailsProps): JSX.Element {
  const location = useLocation();
  const settings = useSettings();
  const { order, transactions } = props.checkoutState;
  const { wasValidated } = order;

  const tippableTotal = gcn.orderManager.getTippableTotal();
  const tipsEnabled = tippableTotal && !!location.tipsLevel;
  const orderIsPartiallyPaid = transactions.length > 0;

  const allowZeroDollarOrders = settings.allowZeroDollarOrders;

  const [savedCards, setSavedCards] = React.useState<
    FreedomPayEcommUtils.SavedCard[] | undefined
  >();

  const fetchSavedCards = async (): Promise<void> => {
    const storedValueSystem = gcn.location.getStoredValueSystem();
    if (storedValueSystem && StoredValueHelper.storedValueSupportsSavedCards(storedValueSystem)) {
      const hasCustomer =
        GcnCustomerAccountHelper.customerAccountsAreEnabled() &&
        Boolean(gcn.orderManager.getCustomer());

      if (hasCustomer) {
        const cards: FreedomPayEcommUtils.SavedCard[] | void = await new Promise<
          FreedomPayEcommUtils.SavedCard[]
        >((resolve, reject) => {
          gcn.maitred.getCustomerResource('payment-methods', null, (err, data) => {
            if (err) {
              reject(err);
            } else {
              resolve(data.paymentMethods);
            }
          });
        }).catch(() => {});

        if (cards) {
          setSavedCards(cards);
        }
      }
    }
  };

  React.useEffect(() => {
    void fetchSavedCards();
  }, []);

  return (
    <div className="payment-details">
      <IonList>
        <IonItem
          lines="none"
          className="payment-details-header"
        >
          <IonLabel className="title">
            <h3>{str(Strings.PAYMENT_DETAILS)}</h3>
          </IonLabel>
        </IonItem>
      </IonList>
      {wasValidated && tipsEnabled ? (
        <TipSelector
          tippableTotal={tippableTotal}
          selectedTipAmount={
            props.checkoutState.order.wasValidated ? props.checkoutState.order.tipTotal || 0 : 0
          }
          presetTips={location.presetTips}
        />
      ) : undefined}
      {wasValidated && (
        <Loyalty
          orderIsPartiallyPaid={orderIsPartiallyPaid}
          location={location}
          {...props.loyaltyState}
        />
      )}
      {wasValidated && (
        <Coupon
          orderIsPartiallyPaid={orderIsPartiallyPaid}
          couponApplied={props.loyaltyState.couponApplied}
        />
      )}
      {wasValidated && (
        <StoredValuePanel
          location={location}
          transactions={transactions}
          submitOrder={props.onSubmit}
          savedCards={savedCards}
        />
      )}
      {wasValidated && !allowZeroDollarOrders && (
        <EcommPaymentForm
          ecommI9nConfig={props.ecommI9nConfig}
          onSubmit={props.onSubmit}
          checkoutState={props.checkoutState}
        />
      )}
      {wasValidated && allowZeroDollarOrders && (
        <SubmitOrderButton
          enabled={true}
          onClick={() => {
            props.onSubmit();
          }}
        />
      )}
    </div>
  );
}

function OrderDisclaimerAndTotals(params: {
  settings: KioskMenuSettings;
  order: CheckoutState['order'];
  transactions: CheckoutState['transactions'];
}): JSX.Element {
  const { settings, order, transactions } = params;
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {settings.checkoutDisclaimer ? (
        <p
          className="tw-border-b tw-border-t tw-border-[var(--color-light)] tw-py-2 tw-text-lg"
          style={{
            fontFamily: 'var(--ion-font-family)',
          }}
        >
          {settings.checkoutDisclaimer}
        </p>
      ) : null}

      <OrderTotals
        order={order}
        transactions={transactions}
      />
    </div>
  );
}

function EcommPaymentForm(props: {
  ecommI9nConfig: EcommI9nConfig;
  onSubmit(): void;
  checkoutState: Store['checkout'];
}): JSX.Element | null {
  const location = useLocation();
  const settings = useSettings();
  const outstandingAmount = gcn.orderManager.getGrandTotal();
  const requiresEcommPayment = outstandingAmount > 0;
  if (!requiresEcommPayment) {
    return (
      <SubmitOrderButton
        enabled={!!props.checkoutState.order.wasValidated}
        onClick={() => {
          props.onSubmit();
        }}
      />
    );
  }

  const isReadyToSubmitOrder = !requiresEcommPayment || props.checkoutState.ecommPayment.isValid;
  const orderTotalsComponent = (
    <OrderDisclaimerAndTotals
      settings={settings}
      order={props.checkoutState.order}
      transactions={props.checkoutState.transactions}
    />
  );
  switch (props.ecommI9nConfig.system) {
    case IntegrationSystem.FreedomPayFlash:
      return (
        <FreedomPayEcommForm
          ecommI9nConfig={props.ecommI9nConfig}
          onSubmit={props.onSubmit}
          orderTotalsComponent={orderTotalsComponent}
          checkoutState={props.checkoutState}
          isReadyToSubmit={isReadyToSubmitOrder}
          requiresEcommPayment={requiresEcommPayment}
          location={location}
        />
      );
    case IntegrationSystem.Stripe:
      return (
        <StripeEcommForm
          ecommI9nConfig={props.ecommI9nConfig}
          onSubmit={props.onSubmit}
          orderTotalsComponent={orderTotalsComponent}
          checkoutState={props.checkoutState}
          isReadyToSubmit={isReadyToSubmitOrder}
          requiresEcommPayment={requiresEcommPayment}
          location={location}
        />
      );
  }
}
